import { useState } from 'react'
import { ChevronsUpDown, Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from '@/components/ui/command'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/components/ui/popover'
import { phoneCodes, getFlagUrl } from '@/data/phone-codes'

interface PhoneInputWithCodeProps {
	countryCode: string
	phoneNumber: string
	onCountryCodeChange: (code: string) => void
	onPhoneNumberChange: (number: string) => void
	disabled?: boolean
	error?: boolean
}

export function PhoneInputWithCode({
	countryCode,
	phoneNumber,
	onCountryCodeChange,
	onPhoneNumberChange,
	disabled = false,
	error = false,
}: PhoneInputWithCodeProps) {
	const [open, setOpen] = useState(false)
	const selected = phoneCodes.find((p) => p.code === countryCode)

	return (
		<div className="flex gap-2">
			<Popover open={open} onOpenChange={setOpen}>
				<PopoverTrigger asChild>
					<Button
						variant="outline"
						role="combobox"
						aria-expanded={open}
						disabled={disabled}
						className={cn(
							'w-[120px] shrink-0 justify-between',
							error && 'border-red-500',
						)}
					>
						{selected ? (
							<span className="flex items-center gap-1.5 truncate">
								<img
									src={getFlagUrl(selected.code)}
									alt={selected.code}
									className="h-3 w-4 shrink-0 object-cover"
								/>
								<span className="text-xs">{selected.dial}</span>
							</span>
						) : (
							<span className="text-muted-foreground text-xs">Code</span>
						)}
						<ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
					</Button>
				</PopoverTrigger>
				<PopoverContent className="w-[260px] p-0" align="start">
					<Command>
						<CommandInput placeholder="Search country..." />
						<CommandList>
							<CommandEmpty>No country found.</CommandEmpty>
							<CommandGroup>
								{phoneCodes.map((entry) => (
									<CommandItem
										key={entry.code}
										value={`${entry.country} ${entry.dial}`}
										onSelect={() => {
											onCountryCodeChange(entry.code)
											setOpen(false)
										}}
									>
										<Check
											className={cn(
												'mr-2 h-4 w-4',
												countryCode === entry.code ? 'opacity-100' : 'opacity-0',
											)}
										/>
										<img
											src={getFlagUrl(entry.code)}
											alt={entry.code}
											className="mr-2 h-3 w-4 shrink-0 object-cover"
										/>
										<span className="flex-1 truncate">{entry.country}</span>
										<span className="text-muted-foreground ml-2 text-xs">
											{entry.dial}
										</span>
									</CommandItem>
								))}
							</CommandGroup>
						</CommandList>
					</Command>
				</PopoverContent>
			</Popover>

			<Input
				type="tel"
				placeholder="Phone number"
				value={phoneNumber}
				onChange={(e) => onPhoneNumberChange(e.target.value)}
				disabled={disabled}
				error={error}
				className="flex-1"
			/>
		</div>
	)
}
